import React, { FC, ReactElement, useCallback, useContext, useEffect, useState } from 'react'
import { CallRecIntegration } from '../../../../i18n'
import { callRecIntegrationByGUIDURL } from '../../../NonReact/constants/ApiConstants' // Utilities imports.
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { getCMEIDFromTelSys, getPartitionInTelSysFromGUID } from '../../Common/API/ItemGetters'
import { ALL_VALUE } from '../../Common/API/ItemsGetters'
import { requestGetJSON } from '../../../NonReact/common/API/REST/RESTAPIClient' // Commons imports.
import { Modal } from '../../../ReactRouting/Components/Molecules/Modal/Modal'
import { ModalLoading } from '../../Common/Components/ModalLoading'
import { DocTopic } from '../../Common/Constants/Documentation'
import { DEFAULT_CME_ID } from '../../Common/Constants/ServerValues' // Contexts imports.
import { ANY_VALUE_STRING_NUMERIC } from '../../Common/Constants/DefaultValues'
import {
    LanguageContextProvider,
    MessageContextProvider,
    PromptContextProvider,
    ReactState
} from '../../Common/Contexts'
import { CommonFormContextProvider } from '../../Common/Contexts/CommonFormContext'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import { clearModalHelpTopic, setModalHelpTopicID } from '../../Common/Utils/HelpPortal'
import CallRecIntegrationTabs from '../CallRecIntegrationTabs'
import { ApplicationCallRecIntegrationResponse, CallRecIntegrationResponse } from '../Types/Types'
import { JSONResponse } from '../../../NonReact/common/Types/Types'
import { $window } from '../../../NonReact/common/AkixiWindow'

type Props = {
    refreshAction: () => void
    filteredCME: string
    filterTelSysID?: number
    filterPartitionID?: string
    filteredIntegrationGUID?: string
    viewOnly?: boolean
    onModalExited?: () => void
}

type CallRecIntegrationProps = {
    viewOnly?: boolean
    closeModal: () => void
    filteredCME: string
    filteredTelSysID?: string | number
    filteredPartitionID?: string
    filteredCallIntegrationGUID?: string
    refreshAction: () => void
}

const CallRecIntegrationTabsContainer: FC<CallRecIntegrationProps> = (props: CallRecIntegrationProps): ReactElement => {
    const { displayError }: MessageContextType = useContext(MessageContext)
    const [partitionID, setPartitionID]: ReactState<Nullable<number>> = useState<Nullable<number>>(null)
    const [cmeID, setCmeID]: ReactState<Nullable<string>> = useState<Nullable<string>>()
    const [callRecIntegration, setCallRecIntegration]: ReactState<Nullable<CallRecIntegrationResponse>> =
        useState<Nullable<CallRecIntegrationResponse>>(null)

    const {
        filteredCME,
        filteredTelSysID,
        filteredPartitionID,
        filteredCallIntegrationGUID,
        closeModal
    }: CallRecIntegrationProps = props

    const getCMEID = useCallback((telSysID: number | string): void => {
        getCMEIDFromTelSys(telSysID)
            .then(setCmeID)
            .catch((e: Error): void => {
                displayError(e)
                setCmeID(DEFAULT_CME_ID)
            })
    }, [])

    useEffect((): void => {
        if (filteredCallIntegrationGUID) {
            // Fetch the currently selected directory.
            requestGetJSON<ApplicationCallRecIntegrationResponse>(
                callRecIntegrationByGUIDURL(filteredCallIntegrationGUID)
            )
                .then((response: JSONResponse<ApplicationCallRecIntegrationResponse>): void => {
                    const data: Definable<CallRecIntegrationResponse> = response?.Data
                    if (data !== undefined) {
                        setCallRecIntegration(data)
                    }
                })
                .catch((e: Error) => {
                    displayError(e)
                    closeModal()
                })
        }
    }, [])

    useEffect((): void => {
        if (isVarNotEmpty(callRecIntegration)) getCMEID(callRecIntegration.TelSysID)
    }, [callRecIntegration?.TelSysID])

    useEffect((): void => {
        if (isVarNotEmpty(filteredTelSysID) && filteredTelSysID !== ANY_VALUE_STRING_NUMERIC) getCMEID(filteredTelSysID)

        if (isVarNotEmpty(filteredPartitionID)) {
            getPartitionInTelSysFromGUID(filteredPartitionID)
                .then(setPartitionID)
                .catch((e: Error): void => {
                    displayError(e)
                    setPartitionID(ALL_VALUE)
                })
        } else {
            setPartitionID(ALL_VALUE)
        }
    }, [])

    if (
        (!IsVarEmpty(filteredCallIntegrationGUID) && callRecIntegration === null) ||
        (isVarNotEmpty(filteredCallIntegrationGUID) && cmeID === undefined)
    ) {
        return <ModalLoading tabs={3} sections={3} />
    }

    return partitionID !== null ? (
        <CallRecIntegrationTabs
            {...props}
            selectedCallRecIntegration={IsVarEmpty(callRecIntegration) ? undefined : callRecIntegration}
            filters={{
                cme: IsVarEmpty(filteredCallIntegrationGUID) ? filteredCME : cmeID ?? DEFAULT_CME_ID,
                telSysID: parseInt(`${filteredTelSysID}`, 10),
                partitionID: partitionID ?? undefined
            }}
        />
    ) : (
        <ModalLoading tabs={3} sections={3} />
    )
}

const ModifyCallRecIntegration: FC<Props> = ({
    filteredCME,
    filterTelSysID,
    filterPartitionID,
    filteredIntegrationGUID,
    refreshAction,
    viewOnly,
    onModalExited
}: Props): ReactElement => {
    // Access language resources directly without context as this is top level component.
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(CallRecIntegration.NAME)

    // Whether the modal window is open.
    const [isModalOpen, setModalOpen] = useState<boolean>(true)

    const closeModal = (): void => {
        $window.disableHotKeys = false
        // Component is controlled externally.
        setModalOpen(false)
    }

    return (
        <div>
            <CommonFormContextProvider>
                <Modal
                    isOpen={isModalOpen}
                    closeOnBackdropClick={true}
                    isClosableWithXButton={true}
                    inlineExitPromptSettings={{
                        message: language.get(CallRecIntegration.CallRecIntegrationAddModify_PromptMessage),
                        confirmButtonText: language.get(CallRecIntegration.CallRecIntegrationAddModify_PromptConfirm),
                        cancelButtonText: language.get(CallRecIntegration.CallRecIntegrationAddModify_PromptCancel),
                        isRouterMode: true
                    }}
                    onOpen={(): void => {
                        // Add contextual doc topic ID to the window object which is used
                        // in old JSP logic that handles F1 key press.
                        setModalHelpTopicID(DocTopic.ADMINCALLRECINT)
                    }}
                    onClose={closeModal}
                    onExited={(): void => {
                        // Remove the contextual doc topic ID from the window that is used
                        // in old JSP logic that handles F1 key press.
                        // When removed the previous logic will work and the modal window doc topic will be not considered.
                        clearModalHelpTopic()
                        onModalExited?.()
                    }}
                >
                    {isModalOpen ? (
                        <LanguageContextProvider>
                            <PromptContextProvider isRouterMode>
                                <MessageContextProvider>
                                    <div className='modal-title-report-config'>
                                        {filteredIntegrationGUID
                                            ? language.get(CallRecIntegration.ModifyCallRecIntegrationTitle)
                                            : language.get(CallRecIntegration.AddCallRecIntegrationTitle)}
                                    </div>
                                    <div className='call-rec-integration-modal'>
                                        <CallRecIntegrationTabsContainer
                                            filteredCME={filteredCME}
                                            filteredTelSysID={filterTelSysID}
                                            filteredPartitionID={filterPartitionID}
                                            filteredCallIntegrationGUID={filteredIntegrationGUID}
                                            viewOnly={viewOnly}
                                            closeModal={closeModal}
                                            refreshAction={refreshAction}
                                        />
                                    </div>
                                </MessageContextProvider>
                            </PromptContextProvider>
                        </LanguageContextProvider>
                    ) : (
                        <> </>
                    )}
                </Modal>
            </CommonFormContextProvider>
        </div>
    )
}

export default ModifyCallRecIntegration
